import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { SellerStatementParams } from '../services/sellersService/sellerStatement'
import { sellerService } from '../services/sellersService'
import { useAuth } from './useAuth'
import { useToast } from '../../view/components/ui/use-toast'

const DEFAULT_STATEMENT_LIMIT = 7
const getDefaultStatementParams = (): SellerStatementParams => ({
  startDate: new Date().toISOString(),
  limit: DEFAULT_STATEMENT_LIMIT,
})

export const useSellerStatement = (sellerId?: string) => {
  const [params, setParams] = useState<SellerStatementParams>(
    getDefaultStatementParams,
  )

  const { isAdmin, isSuperAdmin, isSupport } = useAuth()
  const { toast } = useToast()

  const queryParams = useMemo(() => {
    const canSendUserId = isAdmin || isSuperAdmin || isSupport

    return {
      ...params,
      userId: canSendUserId ? sellerId : undefined,
    }
  }, [isAdmin, isSuperAdmin, isSupport, params, sellerId])

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ['sellerStatement', sellerId, queryParams],
    queryFn: async ({ pageParam }: { pageParam: string }) =>
      sellerService.sellerStatement({
        ...queryParams,
        startDate: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (queryParams.endDate || !lastPage.hasNext || !lastPage.endDate) {
        return undefined
      }

      return lastPage.endDate
    },
    initialPageParam: queryParams.startDate ?? new Date().toISOString(),
    enabled: Boolean(sellerId),
  })

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregador os dados, tente novamente mais tarde!',
      })
    }
  }, [isError, toast])

  const resetParams = useCallback(() => {
    setParams(getDefaultStatementParams())
  }, [])

  return {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    params,
    setParams,
    resetParams,
  }
}
